<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import type { Component } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { BookOpen, GraduationCap } from 'lucide-vue-next';
import * as LucideIcons from 'lucide-vue-next';
import {
  TRAINING_CENTRE_LOCATION_FILTER_ALL,
  collectDistrictFilterOptionsFromCentres,
  collectSubdistrictFilterOptionsForDistrict,
  distinctTrainingCentreProgramListLabels,
  resolveAccreditationVisualStatus,
  trainingCentreLocationHierarchyLine,
  trainingCentreLocationLabel,
  trainingCentreLocationMatchesAdminFilters,
  trainingCentreProgramLinkListLabel,
  trainingCentreRowMatchesLocationAdminFilters,
  type TrainingCentreAccreditationLevelRow,
  type TrainingCentreRow,
} from '@/db/trainingCentres';
import type { SupportedLocale } from '@/i18n';
import { useContentStore } from '@/stores/content';
import { useTrainingCentresStore } from '@/stores/trainingCentres';
import TrainingCentresDirectoryMap from '@/components/TrainingCentresDirectoryMap.vue';

const ALL = TRAINING_CENTRE_LOCATION_FILTER_ALL;

const SearchIcon = (LucideIcons as Record<string, Component>).Search;
const MapPinIcon = (LucideIcons as Record<string, Component>).MapPin;
const ArrowRightIcon = (LucideIcons as Record<string, Component>).ArrowRight;
const ChevronDownIcon = (LucideIcons as Record<string, Component>).ChevronDown;

const props = defineProps<{
  filter_card_title?: string | null;
}>();

const { t, locale } = useI18n();
const route = useRoute();
const router = useRouter();
const contentStore = useContentStore();
const trainingCentresStore = useTrainingCentresStore();

const directoryMapRef = ref<InstanceType<typeof TrainingCentresDirectoryMap> | null>(null);
/** Once opened, keep the map mounted so reopening the disclosure does not recreate tiles. */
const directoryMapMounted = ref(false);

const searchQuery = ref('');
const selectedDistrict = ref(ALL);
const selectedSubdistrict = ref(ALL);
const selectedLevelId = ref(ALL);

watch(selectedDistrict, () => {
  selectedSubdistrict.value = ALL;
});

onMounted(() => {
  void trainingCentresStore.initialize();
});

function levelDisplayName(level: TrainingCentreAccreditationLevelRow | null): string {
  if (!level) return '';
  const loc = locale.value as SupportedLocale;
  if (loc === 'en') {
    return level.name_en || level.name_tet || level.name_pt || '';
  }
  if (loc === 'pt') {
    return level.name_pt || level.name_en || level.name_tet || '';
  }
  return level.name_tet || level.name_en || level.name_pt || '';
}

function levelBadgeModifier(level: TrainingCentreAccreditationLevelRow | null): string {
  const k = resolveAccreditationVisualStatus(level);
  if (k === 'accredited') return 'training-centre-list-block__badge--status-accredited';
  if (k === 'licensed') return 'training-centre-list-block__badge--status-licensed';
  return 'training-centre-list-block__badge--status-muted';
}

function municipalityLine(org: TrainingCentreRow): string {
  const names = (org.locations ?? [])
    .map((l) => trainingCentreLocationLabel(l))
    .filter(Boolean);
  if (!names.length) return '';
  return names.join(', ');
}

const districtFilterOptions = computed(() =>
  collectDistrictFilterOptionsFromCentres(trainingCentresStore.items)
);

const subdistrictFilterOptions = computed(() =>
  collectSubdistrictFilterOptionsForDistrict(
    trainingCentresStore.items,
    selectedDistrict.value
  )
);

const levelOptions = computed(() => {
  const map = new Map<number, TrainingCentreAccreditationLevelRow>();
  for (const org of trainingCentresStore.items) {
    const lvl = org.accreditation_level;
    if (lvl) map.set(lvl.id, lvl);
  }
  return [...map.values()].sort((a, b) => {
    const sa = a.sort_order ?? 999;
    const sb = b.sort_order ?? 999;
    if (sa !== sb) return sa - sb;
    return levelDisplayName(a).localeCompare(levelDisplayName(b), undefined, {
      sensitivity: 'base',
    });
  });
});

const filteredTrainingCentreCards = computed(() => {
  const q = searchQuery.value.trim().toLowerCase();
  const levelId = selectedLevelId.value;

  const filtered = trainingCentresStore.sortedItems.filter((org) => {
    if (q) {
      const name = (org.name || '').toLowerCase();
      const kodigu = (org.kodigu || '').toLowerCase();
      const programMatch = (org.programs ?? []).some((link) =>
        trainingCentreProgramLinkListLabel(link).toLowerCase().includes(q)
      );
      if (!name.includes(q) && !kodigu.includes(q) && !programMatch) return false;
    }
    if (
      !trainingCentreRowMatchesLocationAdminFilters(
        org,
        selectedDistrict.value,
        selectedSubdistrict.value
      )
    ) {
      return false;
    }
    if (levelId !== ALL) {
      const id = Number(levelId);
      if (!org.accreditation_level || org.accreditation_level.id !== id) return false;
    }
    return true;
  });

  return filtered
    .map((org) => {
      const programNames = distinctTrainingCentreProgramListLabels(org);
      return {
        org,
        municipality: municipalityLine(org),
        programNames,
        programCount: programNames.length,
      };
    })
    .sort((a, b) => {
      if (b.programCount !== a.programCount) return b.programCount - a.programCount;
      return (a.org.name || '').localeCompare(b.org.name || '', undefined, {
        sensitivity: 'base',
      });
    })
    .map(({ org, municipality, programNames, programCount }) => ({
      org,
      municipality,
      programNames,
      programCount,
    }));
});

function coursesLabel(count: number): string {
  if (count === 1) return t('trainingCentreList.courseCountOne');
  return t('trainingCentreList.courseCountMany', { count });
}

const filterCardTitle = computed(() => {
  const custom = (props.filter_card_title || '').trim();
  return custom || t('trainingCentreList.filterHeading');
});

function routeObjectForHash(hash: string) {
  return {
    path: route.path,
    query: route.query,
    hash: `#${hash}`,
  };
}

function detailsLinkTo(org: TrainingCentreRow) {
  const pid = org.detail_page_id;
  if (typeof pid === 'number' && pid > 0) {
    const url = contentStore.getUrlForPageId(pid);
    if (url && url !== '/') return url;
  }
  return routeObjectForHash(`training-centre-${org.id}`);
}

const directoryMapPins = computed(() => {
  type Pin = {
    lat: number;
    lng: number;
    title: string;
    subtitle?: string;
    href: string;
  };
  const out: Pin[] = [];
  const d = selectedDistrict.value;
  const s = selectedSubdistrict.value;
  for (const { org } of filteredTrainingCentreCards.value) {
    const withPoint = (org.locations ?? []).filter((loc) => {
      const p = loc.point;
      if (p == null || typeof p.lat !== 'number' || typeof p.lng !== 'number') return false;
      return Number.isFinite(p.lat) && Number.isFinite(p.lng);
    });
    const locForPin =
      withPoint.find((loc) => trainingCentreLocationMatchesAdminFilters(loc, d, s)) ??
      (d === ALL && s === ALL ? withPoint[0] : undefined);
    if (!locForPin?.point) continue;
    const href = router.resolve(detailsLinkTo(org)).href;
    out.push({
      lat: locForPin.point.lat,
      lng: locForPin.point.lng,
      title: (org.name || '').trim() || t('trainingCentreDetail.mapPinFallback'),
      subtitle: trainingCentreLocationHierarchyLine(locForPin) || undefined,
      href,
    });
  }
  return out;
});

function onDirectoryMapToggle(ev: Event) {
  const el = ev.target as HTMLDetailsElement;
  if (el.open) {
    if (!directoryMapMounted.value) directoryMapMounted.value = true;
    nextTick(() => {
      requestAnimationFrame(() => directoryMapRef.value?.relayout());
    });
  }
}
</script>

<template>
  <section
    class="training-centre-list-block"
    :aria-label="t('trainingCentreList.sectionAria')"
  >
    <div class="training-centre-list-block__filter-card">
      <h2 class="training-centre-list-block__filter-heading">
        <SearchIcon class="training-centre-list-block__filter-icon" :size="20" aria-hidden="true" />
        {{ filterCardTitle }}
      </h2>

      <div class="training-centre-list-block__filters">
        <div class="training-centre-list-block__field">
          <label class="training-centre-list-block__label" for="tc-list-search">{{
            t('trainingCentreList.search')
          }}</label>
          <div class="training-centre-list-block__search-wrap">
            <SearchIcon
              class="training-centre-list-block__search-icon"
              :size="16"
              aria-hidden="true"
            />
            <input
              id="tc-list-search"
              v-model="searchQuery"
              type="search"
              class="training-centre-list-block__input training-centre-list-block__input--search"
              :placeholder="t('trainingCentreList.searchPlaceholder')"
              autocomplete="off"
            />
          </div>
        </div>

        <div class="training-centre-list-block__field">
          <label class="training-centre-list-block__label" for="tc-list-district">{{
            t('trainingCentreList.district')
          }}</label>
          <div class="training-centre-list-block__select-wrap">
            <select
              id="tc-list-district"
              v-model="selectedDistrict"
              class="training-centre-list-block__select"
            >
              <option :value="ALL">{{ t('trainingCentreList.all') }}</option>
              <option
                v-for="opt in districtFilterOptions"
                :key="opt.value"
                :value="opt.value"
              >
                {{ opt.label }}
              </option>
            </select>
            <ChevronDownIcon class="training-centre-list-block__select-chevron" :size="16" aria-hidden="true" />
          </div>
        </div>

        <div class="training-centre-list-block__field">
          <label class="training-centre-list-block__label" for="tc-list-subdistrict">{{
            t('trainingCentreList.subdistrict')
          }}</label>
          <div class="training-centre-list-block__select-wrap">
            <select
              id="tc-list-subdistrict"
              v-model="selectedSubdistrict"
              class="training-centre-list-block__select"
              :disabled="selectedDistrict === ALL"
            >
              <option :value="ALL">{{ t('trainingCentreList.allSubdistricts') }}</option>
              <option
                v-for="opt in subdistrictFilterOptions"
                :key="opt.value"
                :value="opt.value"
              >
                {{ opt.label }}
              </option>
            </select>
            <ChevronDownIcon class="training-centre-list-block__select-chevron" :size="16" aria-hidden="true" />
          </div>
        </div>

        <div class="training-centre-list-block__field">
          <label class="training-centre-list-block__label" for="tc-list-level">{{
            t('trainingCentreList.level')
          }}</label>
          <div class="training-centre-list-block__select-wrap">
            <select id="tc-list-level" v-model="selectedLevelId" class="training-centre-list-block__select">
              <option :value="ALL">{{ t('trainingCentreList.all') }}</option>
              <option v-for="lvl in levelOptions" :key="lvl.id" :value="String(lvl.id)">
                {{ levelDisplayName(lvl) }}
              </option>
            </select>
            <ChevronDownIcon class="training-centre-list-block__select-chevron" :size="16" aria-hidden="true" />
          </div>
        </div>
      </div>

      <p class="training-centre-list-block__count" role="status">
        {{
          t('trainingCentreList.showingCount', {
            shown: filteredTrainingCentreCards.length,
            total: trainingCentresStore.items.length,
          })
        }}
      </p>
      <p v-if="trainingCentresStore.status === 'loading' && trainingCentresStore.items.length === 0" class="training-centre-list-block__hint">
        {{ t('trainingCentreList.loading') }}
      </p>
      <p v-else-if="trainingCentresStore.status === 'error' && trainingCentresStore.items.length === 0" class="training-centre-list-block__hint training-centre-list-block__hint--error">
        {{ trainingCentresStore.error || t('trainingCentreList.loadError') }}
      </p>
    </div>

    <details
      v-if="directoryMapPins.length > 0"
      class="training-centre-list-block__map-details"
      :aria-label="t('trainingCentreList.directoryMapSummaryAria')"
      @toggle="onDirectoryMapToggle"
    >
      <summary class="training-centre-list-block__map-summary">
        <MapPinIcon class="training-centre-list-block__map-summary-icon" :size="18" aria-hidden="true" />
        <span class="training-centre-list-block__map-summary-label">{{ t('trainingCentreList.directoryMapTitle') }}</span>
        <span class="training-centre-list-block__map-summary-count">({{ directoryMapPins.length }})</span>
        <ChevronDownIcon class="training-centre-list-block__map-chevron" :size="18" aria-hidden="true" />
      </summary>
      <div class="training-centre-list-block__map-panel">
        <TrainingCentresDirectoryMap
          v-if="directoryMapMounted"
          ref="directoryMapRef"
          :pins="directoryMapPins"
        />
      </div>
    </details>

    <div class="training-centre-list-block__grid">
      <RouterLink
        v-for="{ org, municipality, programNames, programCount } in filteredTrainingCentreCards"
        :id="`training-centre-${org.id}`"
        :key="org.id"
        :to="detailsLinkTo(org)"
        class="training-centre-list-block__card"
        :aria-labelledby="`tc-list-card-title-${org.id}`"
      >
        <div class="training-centre-list-block__card-top">
          <div class="training-centre-list-block__avatar" aria-hidden="true">
            <GraduationCap :size="24" />
          </div>
          <div class="training-centre-list-block__badges">
            <span
              v-if="org.accreditation_level"
              class="training-centre-list-block__badge"
              :class="levelBadgeModifier(org.accreditation_level)"
            >
              {{ levelDisplayName(org.accreditation_level) }}
            </span>
          </div>
        </div>

        <h3 :id="`tc-list-card-title-${org.id}`" class="training-centre-list-block__card-title">
          {{ org.name }}
        </h3>

        <ul
          v-if="municipality || programCount > 0"
          class="training-centre-list-block__meta"
        >
          <li v-if="municipality" class="training-centre-list-block__meta-row">
            <MapPinIcon class="training-centre-list-block__meta-icon" :size="16" aria-hidden="true" />
            <span>{{ municipality }}</span>
          </li>
          <li
            v-if="programCount > 0"
            class="training-centre-list-block__meta-row"
            :aria-label="`${t('trainingCentreList.programsOfferedAria')}: ${programNames.join(', ')}`"
          >
            <BookOpen class="training-centre-list-block__meta-icon" :size="16" aria-hidden="true" />
            <span>{{ coursesLabel(programCount) }}</span>
          </li>
        </ul>

        <span class="training-centre-list-block__details">
          {{ t('trainingCentreList.viewDetails') }}
          <ArrowRightIcon class="training-centre-list-block__details-arrow" :size="16" aria-hidden="true" />
        </span>
      </RouterLink>
    </div>

    <div
      v-if="
        filteredTrainingCentreCards.length === 0 &&
        trainingCentresStore.status !== 'loading' &&
        trainingCentresStore.items.length > 0
      "
      class="training-centre-list-block__empty"
    >
      <div class="training-centre-list-block__empty-icon-wrap" aria-hidden="true">
        <SearchIcon :size="32" />
      </div>
      <h3 class="training-centre-list-block__empty-title">{{ t('trainingCentreList.noResultsTitle') }}</h3>
      <p class="training-centre-list-block__empty-text">{{ t('trainingCentreList.noResultsHint') }}</p>
    </div>

    <div
      v-if="
        trainingCentresStore.items.length === 0 &&
        trainingCentresStore.status !== 'loading' &&
        trainingCentresStore.status !== 'uninitialized'
      "
      class="training-centre-list-block__empty"
    >
      <p class="training-centre-list-block__empty-text">{{ t('trainingCentreList.noTrainingCentres') }}</p>
    </div>
  </section>
</template>

<style scoped>
.training-centre-list-block {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.training-centre-list-block__filter-card {
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: 6px;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.training-centre-list-block__filter-heading {
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--color-text);
}

.training-centre-list-block__filter-icon {
  flex-shrink: 0;
  color: var(--color-primary);
}

.training-centre-list-block__filters {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
}

@media (min-width: 768px) {
  .training-centre-list-block__filters {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (min-width: 1200px) {
  .training-centre-list-block__filters {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}

.training-centre-list-block__label {
  display: block;
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--color-text);
}

.training-centre-list-block__search-wrap {
  position: relative;
}

.training-centre-list-block__search-icon {
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--color-text-muted);
  pointer-events: none;
}

.training-centre-list-block__input,
.training-centre-list-block__select {
  width: 100%;
  box-sizing: border-box;
  font: inherit;
  font-size: 0.875rem;
  line-height: 1.4;
  color: var(--color-text);
  outline: none;
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease;
}

.training-centre-list-block__input {
  /* Match select vertical padding so search and dropdowns share one row height (prototype parity). */
  padding: 0.625rem 1rem;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: 6px;
}

.training-centre-list-block__select-wrap {
  position: relative;
  cursor: pointer;
}

.training-centre-list-block__select {
  padding: 0.625rem 2.5rem 0.625rem 1rem;
  appearance: none;
  cursor: pointer;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: 6px;
}

.training-centre-list-block__select:hover,
.training-centre-list-block__select:focus,
.training-centre-list-block__select:focus-visible,
.training-centre-list-block__select:active {
  cursor: pointer;
}

.training-centre-list-block__select-chevron {
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--color-text-muted);
  pointer-events: none;
}

.training-centre-list-block__input--search {
  padding-left: 2.5rem;
}

.training-centre-list-block__input:focus,
.training-centre-list-block__select:focus {
  border-color: var(--color-primary);
  box-shadow: 0 0 0 3px var(--color-focus-ring);
}

.training-centre-list-block__count {
  margin: 0;
  font-size: 0.875rem;
  color: var(--color-text-muted);
}

.training-centre-list-block__hint {
  margin: 0;
  font-size: 0.875rem;
  color: var(--color-text-muted);
}

.training-centre-list-block__hint--error {
  color: var(--color-error);
}

.training-centre-list-block__map-details {
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: 6px;
  overflow: hidden;
}

.training-centre-list-block__map-summary {
  list-style: none;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem 1.25rem;
  font-size: 1rem;
  font-weight: 600;
  color: var(--color-text);
  cursor: pointer;
  user-select: none;
}

.training-centre-list-block__map-summary::-webkit-details-marker {
  display: none;
}

.training-centre-list-block__map-summary::marker {
  content: '';
}

.training-centre-list-block__map-summary-icon {
  flex-shrink: 0;
  color: var(--color-primary);
}

.training-centre-list-block__map-summary-label {
  min-width: 0;
}

.training-centre-list-block__map-summary-count {
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--color-text-muted);
}

.training-centre-list-block__map-chevron {
  margin-left: auto;
  flex-shrink: 0;
  color: var(--color-text-muted);
  transition: transform 0.2s ease;
}

.training-centre-list-block__map-details[open] .training-centre-list-block__map-chevron {
  transform: rotate(180deg);
}

.training-centre-list-block__map-panel {
  border-top: 1px solid var(--color-border);
}

.training-centre-list-block__grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
}

@media (min-width: 768px) {
  .training-centre-list-block__grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (min-width: 1024px) {
  .training-centre-list-block__grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

.training-centre-list-block__card {
  display: block;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: 6px;
  padding: 1.5rem;
  text-decoration: none;
  color: inherit;
  cursor: pointer;
  transition:
    box-shadow 0.3s ease,
    transform 0.3s ease,
    color 0.3s ease;
}

.training-centre-list-block__card:focus-visible {
  outline: 2px solid var(--color-primary);
  outline-offset: 2px;
}

.training-centre-list-block__card:hover {
  box-shadow:
    0 10px 15px -3px rgba(0, 0, 0, 0.1),
    0 4px 6px -4px rgba(0, 0, 0, 0.1);
  transform: translateY(-4px);
}

.training-centre-list-block__card-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.training-centre-list-block__avatar {
  width: 3rem;
  height: 3rem;
  border-radius: 6px;
  background: color-mix(in srgb, var(--color-primary) 10%, transparent);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: var(--color-primary);
}

.training-centre-list-block__badges {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.5rem;
}

.training-centre-list-block__badge {
  display: inline-block;
  padding: 0.375rem 0.75rem;
  border-radius: 3px;
  font-size: 0.75rem;
  font-weight: 600;
  border: 1px solid transparent;
}

/* Tailwind green-100 / green-700 / green-200 — prototype “Accredited” */
.training-centre-list-block__badge--status-accredited {
  background: #dcfce7;
  color: #15803d;
  border-color: #bbf7d0;
}

/* Tailwind blue-100 / blue-700 / blue-200 — prototype “Licensed” */
.training-centre-list-block__badge--status-licensed {
  background: #dbeafe;
  color: #1d4ed8;
  border-color: #bfdbfe;
}

/* Tailwind gray-100 / gray-700 / gray-200 — prototype “Non-Accredited” and other labels */
.training-centre-list-block__badge--status-muted {
  background: #f3f4f6;
  color: #374151;
  border-color: #e5e7eb;
}

.training-centre-list-block__card-title {
  margin: 0 0 1rem;
  font-size: 1.125rem;
  font-weight: 600;
  line-height: 1.3;
  color: var(--color-text);
  transition: color 0.3s ease;
}

.training-centre-list-block__card:hover .training-centre-list-block__card-title {
  color: var(--color-primary);
}

.training-centre-list-block__meta {
  margin: 0 0 1.25rem;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
}

.training-centre-list-block__meta-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: var(--color-text-muted);
  min-width: 0;
}

.training-centre-list-block__meta-row > span {
  min-width: 0;
}

.training-centre-list-block__meta-icon {
  flex-shrink: 0;
  color: var(--color-text-muted);
}

.training-centre-list-block__details {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--color-primary);
  transition: gap 0.3s ease;
}

.training-centre-list-block__card:hover .training-centre-list-block__details {
  gap: 0.5rem;
}

.training-centre-list-block__details-arrow {
  flex-shrink: 0;
}

.training-centre-list-block__empty {
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: 6px;
  padding: 3rem 1.5rem;
  text-align: center;
}

.training-centre-list-block__empty-icon-wrap {
  width: 4rem;
  height: 4rem;
  margin: 0 auto 1rem;
  border-radius: 9999px;
  background: var(--color-bg-muted);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text-muted);
}

.training-centre-list-block__empty-title {
  margin: 0 0 0.5rem;
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--color-text);
}

.training-centre-list-block__empty-text {
  margin: 0;
  font-size: 0.875rem;
  color: var(--color-text-muted);
}
</style>
